import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useTranslationTyped } from 'i18n/i18n'
import type { Ref } from 'react'
import type { GradeAppealActionBlockedReason } from './useGradeAppeal'

export function GradeAppealActionButton({
  isPending,
  canShow,
  canFile,
  blockedReason,
  onClick,
  buttonRef,
}: {
  isPending: boolean
  canShow: boolean
  canFile: boolean
  blockedReason: GradeAppealActionBlockedReason | null
  onClick: () => void
  buttonRef?: Ref<HTMLButtonElement | null>
}) {
  const { tt } = useTranslationTyped()

  if (isPending) {
    return (
      <BreakoutButton
        kind="secondary"
        loading
        disabled
        data-testid="grade-appeal-button-loading"
      >
        {tt.meeting.results.appeal.action()}
      </BreakoutButton>
    )
  }

  if (!canShow) return null

  if (!blockedReason) {
    return (
      <BreakoutButton
        kind="secondary"
        disabled={!canFile}
        data-testid="grade-appeal-button"
        onClick={onClick}
        ref={buttonRef}
      >
        {tt.meeting.results.appeal.action()}
      </BreakoutButton>
    )
  }

  const tooltip =
    blockedReason === 'not_graded'
      ? tt.meeting.results.appeal.not_graded_tooltip()
      : tt.meeting.results.appeal.window_closed_tooltip()

  // A natively disabled button cannot take focus, so keyboard users could not
  // reach the tooltip that explains why. aria-disabled keeps it focusable while
  // screen readers still announce it as unavailable; it has no click handler.
  return (
    <BreakoutTooltip content={tooltip}>
      <BreakoutButton
        kind="secondary"
        aria-disabled
        ripple={false}
        className="aria-disabled:cursor-default aria-disabled:opacity-30"
        data-testid="grade-appeal-button"
        ref={buttonRef}
      >
        {tt.meeting.results.appeal.action()}
      </BreakoutButton>
    </BreakoutTooltip>
  )
}
